import React, { createContext, useContext, useState, useEffect } from 'react';
import { defaultLanguage, getTranslation, getCategories, getQuickItems } from '../utils/translations';
import { useAuth } from './AuthContext';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { user, updateUser } = useAuth();
  
  // UI Language (interface)
  const [uiLanguage, setUILanguageState] = useState(() => {
    // Check if user has explicitly set a language preference
    const hasExplicitLanguage = localStorage.getItem('listWizLanguageExplicitlySet') === 'true';
    const savedLanguage = localStorage.getItem('listWizLanguage');
    
    // If no explicit preference, always use default (English)
    // This ensures new users always start with English
    if (!hasExplicitLanguage) {
      console.log('LanguageContext: No explicit UI language preference, using default:', defaultLanguage);
      return defaultLanguage;
    }
    
    console.log('LanguageContext: Using explicit UI language preference:', savedLanguage);
    return savedLanguage || defaultLanguage;
  });

  // Content Language (items, categories)
  const [contentLanguage, setContentLanguageState] = useState(() => {
    const savedContentLanguage = localStorage.getItem('listWizContentLanguage');
    console.log('LanguageContext: Initializing content language:', savedContentLanguage || uiLanguage);
    return savedContentLanguage || defaultLanguage;
  });

  // Backwards compatibility - keep 'language' as UI language
  const language = uiLanguage;

  const [customCategories, setCustomCategories] = useState(() => {
    // Get custom categories from localStorage
    const saved = localStorage.getItem('listWizCustomCategories');
    return saved ? JSON.parse(saved) : [];
  });

  // Track when user is actively changing language to prevent conflicts
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  // Update languages when user data changes (on login/registration)
  useEffect(() => {
    // Don't override if user is actively changing language
    if (isChangingLanguage) {
      console.log('LanguageContext: Skipping user sync while language is being changed');
      return;
    }
    
    if (user && user.uiLanguage && user.uiLanguage !== uiLanguage) {
      console.log('LanguageContext: Setting UI language from user profile:', user.uiLanguage);
      setUILanguageState(user.uiLanguage);
    }
    if (user && user.contentLanguage && user.contentLanguage !== contentLanguage) {
      console.log('LanguageContext: Setting content language from user profile:', user.contentLanguage);
      setContentLanguageState(user.contentLanguage);
    }
  }, [user, uiLanguage, contentLanguage, isChangingLanguage]);

  const setUILanguage = async (newLanguage) => {
    console.log('LanguageContext: Changing UI language to:', newLanguage);
    setIsChangingLanguage(true); // Prevent useEffect conflicts
    setUILanguageState(newLanguage);
    
    // Mark that user has explicitly set a language preference
    localStorage.setItem('listWizLanguageExplicitlySet', 'true');
    // Save to localStorage (for non-authenticated users)
    localStorage.setItem('listWizLanguage', newLanguage);
    
    // Save to user profile if authenticated
    if (user) {
      try {
        await updateUser({ uiLanguage: newLanguage });
        console.log('LanguageContext: UI language saved to user profile');
      } catch (error) {
        console.error('LanguageContext: Failed to save UI language to user profile:', error);
      }
    }
    
    // Reset the flag after a short delay to allow for updates
    setTimeout(() => {
      setIsChangingLanguage(false);
    }, 500);
  };

  const setContentLanguage = async (newLanguage) => {
    console.log('LanguageContext: Changing content language to:', newLanguage);
    setIsChangingLanguage(true); // Prevent useEffect conflicts
    setContentLanguageState(newLanguage);
    
    // Save to localStorage
    localStorage.setItem('listWizContentLanguage', newLanguage);
    
    // Save to user profile if authenticated
    if (user) {
      try {
        await updateUser({ contentLanguage: newLanguage });
        console.log('LanguageContext: Content language saved to user profile');
      } catch (error) {
        console.error('LanguageContext: Failed to save content language to user profile:', error);
      }
    }
    
    // Reset the flag after a short delay
    setTimeout(() => {
      setIsChangingLanguage(false);
    }, 500);
  };

  // Backwards compatibility function
  const setLanguage = setUILanguage;

  useEffect(() => {
    // Save language to localStorage
    localStorage.setItem('listWizLanguage', language);
  }, [language]);

  useEffect(() => {
    // Save custom categories to localStorage
    localStorage.setItem('listWizCustomCategories', JSON.stringify(customCategories));
  }, [customCategories]);

  const t = (key, defaultValue) => {
    const result = getTranslation(key, uiLanguage, defaultValue);
    // Extra safety check
    if (typeof result === 'object' && result !== null) {
      console.error(`Translation error: key "${key}" returned object:`, result);
      return defaultValue || key;
    }
    return result;
  };

  // Add content-specific functions
  const getContentCategories = () => {
    return getCategories(contentLanguage);
  };

  const getContentQuickItems = () => {
    return getQuickItems(contentLanguage);
  };

  // Add quickItems accessor to t function for backwards compatibility
  t.quickItems = getContentQuickItems();
  t.language = uiLanguage;

  const getAllCategories = () => {
    const contentCategories = getContentCategories();
    const allCategories = { ...contentCategories };
    
    // Add custom categories
    customCategories.forEach(category => {
      allCategories[category] = category;
    });
    
    return allCategories;
  };

  const addCustomCategory = (categoryName) => {
    if (categoryName && !customCategories.includes(categoryName)) {
      setCustomCategories(prev => [...prev, categoryName]);
      return true;
    }
    return false;
  };

  const removeCustomCategory = (categoryName) => {
    setCustomCategories(prev => prev.filter(cat => cat !== categoryName));
  };

  const value = {
    // UI Language (interface)
    uiLanguage,
    setUILanguage,
    
    // Content Language (items, categories)
    contentLanguage,
    setContentLanguage,
    getContentCategories,
    getContentQuickItems,
    
    // Backwards compatibility
    language,
    setLanguage,
    t,
    getAllCategories,
    customCategories,
    addCustomCategory,
    removeCustomCategory
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
